<template>
  <div class="lead-funnel" :class="{ 'lead-funnel--embedded': embedded }">
    <div class="lead-funnel__inner">
      <div class="lead-funnel__panel">
        <template v-if="stage === 'quiz'">
          <div
            class="lead-funnel__progress"
            role="progressbar"
            :aria-valuenow="quizStep + 1"
            :aria-valuemax="LANDING_QUIZ_ITEMS.length"
          >
            <div class="lead-funnel__progress-bar" :style="{ width: quizProgressPercent }" />
          </div>
          <h2 class="lead-funnel__title lead-funnel__title--md">{{ currentQuiz.question }}</h2>

          <div v-if="currentQuiz.type === 'text'" class="lead-funnel__quiz-text">
            <input
              v-model.trim="textDraft"
              type="text"
              class="lead-funnel__input"
              :placeholder="currentQuiz.placeholder || ''"
              @keydown.enter.prevent="submitCurrentStep"
            />
            <p v-if="stepError" class="msg err">{{ stepError }}</p>
            <button type="button" class="btn btn--primary btn--full" :disabled="quizAdvancing" @click="submitCurrentStep">
              Далее
            </button>
          </div>

          <div v-else-if="currentQuiz.type === 'phone'" class="lead-funnel__quiz-text">
            <PhoneInput v-model="phoneDraft" class="lead-funnel__input" />
            <p v-if="stepError" class="msg err">{{ stepError }}</p>
            <button type="button" class="btn btn--primary btn--full" :disabled="quizAdvancing" @click="submitCurrentStep">
              Далее
            </button>
          </div>

          <div v-else-if="currentQuiz.type === 'contact'" class="lead-funnel__quiz-text">
            <div class="lead-funnel__quiz-options">
              <button
                v-for="opt in currentQuiz.options"
                :key="opt.value"
                type="button"
                class="lead-funnel__option"
                :class="{ 'lead-funnel__option--active': contactChannelDraft === opt.value }"
                @click="contactChannelDraft = opt.value"
              >
                {{ opt.label }}
              </button>
            </div>
            <input
              v-if="contactChannelDraft === 'telegram'"
              v-model.trim="telegramDraft"
              type="text"
              class="lead-funnel__input"
              placeholder="@username или ссылка Telegram"
            />
            <EmailInput
              v-if="contactChannelDraft === 'email'"
              v-model="emailDraft"
              class="lead-funnel__input"
              placeholder="name@example.com"
            />
            <p v-if="stepError" class="msg err">{{ stepError }}</p>
            <button type="button" class="btn btn--primary btn--full" :disabled="quizAdvancing" @click="submitCurrentStep">
              Далее
            </button>
          </div>

          <div v-else-if="currentQuiz.type === 'textarea'" class="lead-funnel__quiz-text">
            <textarea
              v-model="textDraft"
              class="lead-funnel__textarea"
              rows="4"
              :placeholder="currentQuiz.placeholder || ''"
            />
            <p v-if="stepError" class="msg err">{{ stepError }}</p>
            <button type="button" class="btn btn--primary btn--full" :disabled="quizAdvancing" @click="submitCurrentStep">
              Далее
            </button>
          </div>

          <div v-else-if="currentQuiz.type === 'choice'" class="lead-funnel__quiz-options">
            <button
              v-for="option in currentQuiz.options"
              :key="option"
              type="button"
              class="lead-funnel__option"
              :disabled="quizAdvancing"
              @click="selectChoiceOption(option)"
            >
              {{ option }}
            </button>
            <p v-if="stepError" class="msg err">{{ stepError }}</p>
          </div>

          <div v-else-if="currentQuiz.type === 'multi'" class="lead-funnel__quiz-multi">
            <label v-for="option in currentQuiz.options" :key="option" class="lead-funnel__multi-label">
              <input v-model="multiDraft" type="checkbox" :value="option" />
              <span>{{ option }}</span>
            </label>
            <p v-if="stepError" class="msg err">{{ stepError }}</p>
            <button
              type="button"
              class="btn btn--primary btn--full"
              :disabled="!multiDraft.length || quizAdvancing"
              @click="submitMultiStep"
            >
              Далее
            </button>
          </div>

          <div v-else-if="currentQuiz.type === 'consent'" class="lead-funnel__quiz-text">
            <PrivacyConsentField v-model="consentAccepted" :show-error="consentError" input-id="quiz-consent" />
            <p v-if="stepError" class="msg err">{{ stepError }}</p>
            <button
              type="button"
              class="btn btn--primary btn--full"
              :disabled="quizAdvancing || submitting"
              @click="submitApplication"
            >
              {{ submitting ? 'Отправка…' : 'Отправить заявку' }}
            </button>
          </div>

          <p class="lead-funnel__quiz-progress">Вопрос {{ quizStep + 1 }} из {{ LANDING_QUIZ_ITEMS.length }}</p>
          <p v-if="quizStep > 0" class="lead-funnel__note">
            <button type="button" class="lead-funnel__back" @click="goBack">← Назад</button>
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import EmailInput from '@/components/form/EmailInput.vue'
import PhoneInput from '@/components/form/PhoneInput.vue'
import PrivacyConsentField from '@/components/PrivacyConsentField.vue'
import { LANDING_QUIZ_ITEMS } from '@/constants/landingQuiz'
import { buildCrmFieldsFromQuiz } from '@/utils/funnelQuiz'
import { normalizeEmail } from '@/utils/emailInput'
import {
  getLeadSessionSeconds,
  markLeadSessionStart,
  sendLeadEvent,
} from '@/utils/crmLead'
import { isRuPhoneComplete } from '@/utils/ruPhoneMask'
import {
  flushMetrikaBeforeNavigate,
  trackSalesMetrikaGoal,
} from '@/utils/metrikaGoals'

defineProps({
  embedded: { type: Boolean, default: false },
})

const emit = defineEmits(['completed'])
const router = useRouter()

const stage = ref('quiz')
const consentAccepted = ref(false)
const consentError = ref(false)
const stepError = ref('')
const quizStep = ref(0)
const quizAnswers = reactive({})
const multiDraft = ref([])
const textDraft = ref('')
const phoneDraft = ref('')
const emailDraft = ref('')
const telegramDraft = ref('')
const contactChannelDraft = ref('')
const quizAdvancing = ref(false)
const submitting = ref(false)
const repeatedVisit = ref(false)
const quizStarted = ref(false)

const currentQuiz = computed(() => LANDING_QUIZ_ITEMS[quizStep.value] || LANDING_QUIZ_ITEMS[0])
const quizProgressPercent = computed(() => `${((quizStep.value + 1) / LANDING_QUIZ_ITEMS.length) * 100}%`)

watch(quizStep, () => {
  stepError.value = ''
  syncQuizDraftForStep()
})

function syncQuizDraftForStep() {
  const item = currentQuiz.value
  if (!item) return
  if (item.type === 'multi') {
    multiDraft.value = Array.isArray(quizAnswers[item.id]) ? [...quizAnswers[item.id]] : []
  } else if (item.type === 'text' || item.type === 'textarea') {
    textDraft.value = String(quizAnswers[item.id] ?? '')
  } else if (item.type === 'phone') {
    phoneDraft.value = String(quizAnswers.phone ?? '')
  } else if (item.type === 'contact') {
    contactChannelDraft.value = quizAnswers.contact_channel || ''
    emailDraft.value = String(quizAnswers.email ?? '')
    telegramDraft.value = String(quizAnswers.telegram ?? '')
  }
}

function baseCrmPayload() {
  return {
    ...buildCrmFieldsFromQuiz(quizAnswers),
    repeated_visit: repeatedVisit.value,
    session_time: getLeadSessionSeconds(),
    registration_completed: false,
    beta_activated: false,
  }
}

function validateCurrentStep() {
  stepError.value = ''
  const item = currentQuiz.value
  const id = item.id

  if (item.type === 'text' || item.type === 'textarea') {
    if (item.required !== false && !textDraft.value.trim()) {
      stepError.value = 'Заполните поле, чтобы продолжить.'
      return false
    }
    return true
  }

  if (item.type === 'phone') {
    if (!isRuPhoneComplete(phoneDraft.value)) {
      stepError.value = 'Укажите полный номер телефона (+7 и 10 цифр).'
      return false
    }
    return true
  }

  if (item.type === 'contact') {
    if (!contactChannelDraft.value) {
      stepError.value = 'Выберите способ связи.'
      return false
    }
    if (contactChannelDraft.value === 'telegram' && !telegramDraft.value.trim()) {
      stepError.value = 'Укажите ник или ссылку Telegram.'
      return false
    }
    if (contactChannelDraft.value === 'email') {
      const em = normalizeEmail(emailDraft.value)
      if (!em || !em.includes('@')) {
        stepError.value = 'Укажите корректный e-mail.'
        return false
      }
      emailDraft.value = em
    }
    return true
  }

  if (item.type === 'multi' && item.required !== false && !multiDraft.value.length) {
    stepError.value = 'Выберите хотя бы один вариант.'
    return false
  }

  if (item.type === 'consent' && !consentAccepted.value) {
    consentError.value = true
    stepError.value = 'Подтвердите согласие на обработку персональных данных.'
    return false
  }

  if (item.type === 'choice' && item.required !== false && !quizAnswers[id]) {
    stepError.value = 'Выберите вариант.'
    return false
  }

  return true
}

function persistCurrentStepAnswers() {
  const item = currentQuiz.value
  const id = item.id

  if (item.type === 'text' || item.type === 'textarea') {
    quizAnswers[id] = textDraft.value.trim()
  } else if (item.type === 'phone') {
    quizAnswers.phone = phoneDraft.value
  } else if (item.type === 'contact') {
    quizAnswers.contact_channel = contactChannelDraft.value
    if (contactChannelDraft.value === 'telegram') {
      quizAnswers.telegram = telegramDraft.value.trim()
      quizAnswers.email = ''
    } else {
      quizAnswers.email = normalizeEmail(emailDraft.value)
      quizAnswers.telegram = ''
    }
  } else if (item.type === 'multi') {
    quizAnswers[id] = [...multiDraft.value]
  }
}

function formatQuizAnswerForCrm(stepId) {
  const raw = quizAnswers[stepId]
  if (stepId === 'contact') {
    const ch = quizAnswers.contact_channel
    const detail = ch === 'telegram' ? quizAnswers.telegram : quizAnswers.email
    return [ch, detail].filter(Boolean).join(': ')
  }
  if (Array.isArray(raw)) return raw.join(', ')
  return String(raw ?? '').trim() || '(пропущено)'
}

function queueQuizStepEvent(stepIndex, stepId, answerText) {
  trackSalesMetrikaGoal('quiz_step')
  void sendLeadEvent('QUIZ_STEP', {
    ...baseCrmPayload(),
    quiz_step_id: stepId,
    quiz_step_index: stepIndex,
    quiz_step_answer: answerText,
  })
}

function ensureQuizStarted() {
  if (quizStarted.value) return
  quizStarted.value = true
  trackSalesMetrikaGoal('quiz_start')
  void sendLeadEvent('NEW_BETA_LEAD', baseCrmPayload())
}

function advanceQuizOrFinish() {
  if (quizStep.value < LANDING_QUIZ_ITEMS.length - 1) {
    quizStep.value += 1
    syncQuizDraftForStep()
    return
  }
}

function applyStepAdvance(recordFn) {
  if (quizAdvancing.value) return
  if (!validateCurrentStep()) return
  quizAdvancing.value = true
  try {
    ensureQuizStarted()
    persistCurrentStepAnswers()
    recordFn?.()
    const stepIndex = quizStep.value
    const stepId = currentQuiz.value.id
    const answerText = formatQuizAnswerForCrm(stepId)
    queueQuizStepEvent(stepIndex, stepId, answerText)
    advanceQuizOrFinish()
  } finally {
    quizAdvancing.value = false
  }
}

function submitCurrentStep() {
  applyStepAdvance()
}

function selectChoiceOption(option) {
  quizAnswers[currentQuiz.value.id] = option
  applyStepAdvance()
}

function submitMultiStep() {
  applyStepAdvance()
}

function goBack() {
  if (quizStep.value > 0) quizStep.value -= 1
}

async function submitApplication() {
  if (submitting.value) return
  if (!validateCurrentStep()) return
  submitting.value = true
  try {
    ensureQuizStarted()
    quizAnswers.consent = true
    const stepIndex = quizStep.value
    const stepId = currentQuiz.value.id
    queueQuizStepEvent(stepIndex, stepId, 'accepted')

    await sendLeadEvent('QUIZ_COMPLETED', baseCrmPayload())
    trackSalesMetrikaGoal('lead_submit')

    emit('completed')
    await flushMetrikaBeforeNavigate()
    await router.push('/thanks')
  } finally {
    submitting.value = false
  }
}

function resetFunnel() {
  stage.value = 'quiz'
  consentAccepted.value = false
  consentError.value = false
  stepError.value = ''
  quizStep.value = 0
  quizAdvancing.value = false
  submitting.value = false
  quizStarted.value = false
  multiDraft.value = []
  textDraft.value = ''
  phoneDraft.value = ''
  emailDraft.value = ''
  telegramDraft.value = ''
  contactChannelDraft.value = ''
  Object.keys(quizAnswers).forEach((k) => delete quizAnswers[k])
  markLeadSessionStart()
  syncQuizDraftForStep()
}

defineExpose({ resetFunnel })

async function onBeforeUnload() {
  if (!quizStarted.value) return
  await sendLeadEvent('SESSION_HEARTBEAT', baseCrmPayload())
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    repeatedVisit.value = window.localStorage.getItem('landing_seen') === '1'
    window.localStorage.setItem('landing_seen', '1')
  }
  markLeadSessionStart()
  syncQuizDraftForStep()
  window.addEventListener('beforeunload', onBeforeUnload)
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', onBeforeUnload)
})
</script>

<style scoped>
.lead-funnel {
  --lf-fg: #1d1d1f;
  --lf-muted: #515154;
  --lf-border: #d2d2d7;
  --lf-surface: #ffffff;
  --lf-accent: #0071e3;
  color: var(--lf-fg);
}

.lead-funnel--embedded .lead-funnel__inner {
  width: 100%;
}

.lead-funnel__inner {
  width: 100%;
}

.lead-funnel__panel {
  width: 100%;
  margin: 0;
  border: 1px solid var(--lf-border);
  border-radius: 16px;
  padding: clamp(1rem, 2.5vw, 1.5rem);
  background: var(--lf-surface);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.lead-funnel__title {
  margin: 0.8rem 0 0.6rem;
  text-align: center;
  font-size: clamp(1.65rem, 3.8vw, 2.6rem);
  line-height: 1.12;
  color: var(--lf-fg);
}

.lead-funnel__title--md {
  font-size: clamp(1.2rem, 2.8vw, 1.65rem);
}

.lead-funnel__input {
  width: 100%;
  margin-bottom: 0.55rem;
  padding: 0.78rem 0.95rem;
  border-radius: 12px;
  border: 1px solid var(--lf-border);
  background: #fff;
  color: var(--lf-fg);
}

.lead-funnel__textarea {
  width: 100%;
  margin-bottom: 0.75rem;
  padding: 0.78rem 0.95rem;
  border-radius: 12px;
  border: 1px solid var(--lf-border);
  background: #fff;
  color: var(--lf-fg);
  resize: vertical;
  min-height: 6rem;
}

.lead-funnel__input.phone-input,
.lead-funnel__input:has(.phone-input__field) {
  padding: 0;
  display: flex;
  align-items: center;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid transparent;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  padding: 0.75rem 1.2rem;
  min-height: 46px;
}

.btn--primary {
  background: #0b6ed8;
  border-color: #54a7ff;
  color: #ffffff;
}

.btn--full {
  width: 100%;
  margin-top: 0.5rem;
}

.btn:disabled {
  opacity: 0.62;
  cursor: not-allowed;
}

.lead-funnel__progress {
  height: 6px;
  border-radius: 999px;
  background: #e5e5ea;
  margin-bottom: 1rem;
  overflow: hidden;
}

.lead-funnel__progress-bar {
  height: 100%;
  background: var(--lf-accent);
  border-radius: 999px;
  transition: width 0.25s ease;
}

.lead-funnel__quiz-options {
  display: grid;
  gap: 0.55rem;
  max-width: 40rem;
  margin: 0 auto;
}

.lead-funnel__quiz-multi,
.lead-funnel__quiz-text {
  max-width: 40rem;
  margin: 0 auto;
}

.lead-funnel__multi-label {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.65rem 0.8rem;
  margin-bottom: 0.45rem;
  border: 1px solid var(--lf-border);
  border-radius: 12px;
  cursor: pointer;
}

.lead-funnel__multi-label:has(input:checked) {
  border-color: var(--lf-accent);
  background: #eaf1ff;
}

.lead-funnel__option {
  border: 1px solid #92b8e8;
  background: #fff;
  color: var(--lf-fg);
  border-radius: 12px;
  min-height: 52px;
  padding: 0.65rem 0.8rem;
  text-align: left;
  cursor: pointer;
}

.lead-funnel__option--active,
.lead-funnel__option:hover {
  border-color: var(--lf-accent);
  background: #eaf1ff;
}

.lead-funnel__option:disabled {
  opacity: 0.65;
  cursor: wait;
}

.lead-funnel__quiz-progress {
  margin: 0.75rem 0 0;
  text-align: center;
  color: var(--lf-muted);
  font-size: 0.82rem;
}

.lead-funnel__note {
  margin: 0.5rem 0 0;
  text-align: center;
}

.lead-funnel__back {
  background: none;
  border: none;
  color: var(--lf-accent);
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: underline;
}

.msg.err {
  color: #d70015;
  font-size: 0.85rem;
}
</style>
